// Argument validation for Ops obligation PA tools (lookup + create).

import {
  isOpsObligationCadence,
  isOpsObligationKind,
  isOpsObligationStatus,
  isOpsUtilitySubtype,
} from "../opsObligations/opsObligationFields.js";
import { nonEmptyString, uuidString } from "./paToolHelpers.js";

export type ObligationToolValidationResult =
  | { ok: true; toolId: "obligation_lookup" | "obligation_create"; args: Record<string, unknown> }
  | { ok: false; error: string };

export function validateObligationLookup(obj: Record<string, unknown>): ObligationToolValidationResult {
  const query = nonEmptyString(obj.query);
  const location_query = nonEmptyString(obj.location_query);
  const obligation_id = obj.obligation_id != null ? uuidString(obj.obligation_id) : null;
  if (obj.obligation_id != null && !obligation_id) {
    return { ok: false, error: "obligation_id must be a valid uuid" };
  }
  if (obj.kind != null && obj.kind !== "" && !isOpsObligationKind(obj.kind)) {
    return { ok: false, error: "kind is invalid" };
  }
  if (obj.utility_subtype != null && obj.utility_subtype !== "" && !isOpsUtilitySubtype(obj.utility_subtype)) {
    return { ok: false, error: "utility_subtype is invalid" };
  }
  if (obj.status != null && obj.status !== "" && !isOpsObligationStatus(obj.status)) {
    return { ok: false, error: "status is invalid" };
  }
  const args: Record<string, unknown> = {};
  if (query) args.query = query.slice(0, 200);
  if (location_query) args.location_query = location_query.slice(0, 200);
  if (obligation_id) args.obligation_id = obligation_id;
  if (isOpsObligationKind(obj.kind)) args.kind = obj.kind;
  if (isOpsUtilitySubtype(obj.utility_subtype)) args.utility_subtype = obj.utility_subtype;
  if (isOpsObligationStatus(obj.status)) args.status = obj.status;
  return { ok: true, toolId: "obligation_lookup", args };
}

function optionalCents(value: unknown): { ok: true; value?: number } | { ok: false; error: string } {
  if (value == null || value === "") return { ok: true };
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return { ok: true, value: Math.round(value) };
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return { ok: true, value: Number(value.trim()) };
  }
  return { ok: false, error: "amount_cents must be a non-negative integer" };
}

export function validateObligationCreate(obj: Record<string, unknown>): ObligationToolValidationResult {
  if (!isOpsObligationKind(obj.kind)) {
    return { ok: false, error: "kind is required (utility, internet, lease, mortgage, or other)" };
  }
  if (obj.kind === "utility") {
    if (obj.utility_subtype != null && obj.utility_subtype !== "" && !isOpsUtilitySubtype(obj.utility_subtype)) {
      return { ok: false, error: "utility_subtype is invalid" };
    }
  }
  if (obj.status != null && obj.status !== "" && !isOpsObligationStatus(obj.status)) {
    return { ok: false, error: "status is invalid" };
  }
  if (obj.cadence != null && obj.cadence !== "" && !isOpsObligationCadence(obj.cadence)) {
    return { ok: false, error: "cadence is invalid" };
  }
  const cents = optionalCents(obj.amount_cents);
  if (!cents.ok) return cents;

  const location_query = nonEmptyString(obj.location_query);
  const provider = nonEmptyString(obj.provider_name);
  const args: Record<string, unknown> = { kind: obj.kind };
  if (obj.kind === "utility" && isOpsUtilitySubtype(obj.utility_subtype)) {
    args.utility_subtype = obj.utility_subtype;
  }
  if (location_query) args.location_query = location_query.slice(0, 200);
  if (obj.org_wide === true) args.org_wide = true;
  if (provider) args.provider_name = provider.slice(0, 200);
  const account = nonEmptyString(obj.account_ref);
  if (account) args.account_ref = account.slice(0, 120);
  const description = nonEmptyString(obj.description);
  if (description) args.description = description.slice(0, 2000);
  const payment = nonEmptyString(obj.payment_url);
  if (payment) args.payment_url = payment.slice(0, 2000);
  if (isOpsObligationCadence(obj.cadence)) args.cadence = obj.cadence;
  const due = nonEmptyString(obj.due_rule);
  if (due) args.due_rule = due.slice(0, 200);
  if (cents.value != null) args.amount_cents = cents.value;
  const responsible = nonEmptyString(obj.responsible_party);
  if (responsible) args.responsible_party = responsible.slice(0, 200);
  const notes = nonEmptyString(obj.notes);
  if (notes) args.notes = notes.slice(0, 8000);
  if (isOpsObligationStatus(obj.status)) args.status = obj.status;
  return { ok: true, toolId: "obligation_create", args };
}
